import "react-image-crop/dist/ReactCrop.css";

import * as React from "react";
import ReactCrop from "react-image-crop";

import {
  fileToString,
  blobToFile
} from "src/library/utils";

export interface CapElementSize {
  width: number;
  height: number;
}
export interface ImageCropperProps {
  src: File;
  onComplete: (file: File) => void;
  cropInsetPercentage?: number;
  aspect?: number;
}

/**
 * Image cropper
 */
export const ImageCropper: React.ReactFCWithChildren<ImageCropperProps> = ({
  cropInsetPercentage = 1, // 100%
  aspect = 3 / 2, // 3:2 aspect ratio equivalent
  src,
  onComplete
}) => {
  const [ imageDataUrl, setImageDataUrl ] = React.useState<string>();
  const [ imgData, setImgData ] = React.useState<HTMLImageElement>();
  const [ crop, setCrop ] = React.useState<ReactCrop.Crop>({ unit: "px" });

  React.useEffect(() => {
    callOnComplete(crop, imgData);
  }, [ crop, imgData ]);

  /**
   * Calls on complete functionality and generates a cropped file
   */
  const callOnComplete = React.useCallback((crop: ReactCrop.Crop, image?: HTMLImageElement) => {
    if (!image) {
      return;
    }
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const originWidth = (crop.width || 0) * scaleX;
    const originHeight = (crop.height || 0) * scaleY;
    // maximum width/height
    const maxWidth = 2000;
    const maxHeight = 2000 / (aspect);

    let targetWidth = originWidth,
      targetHeight = originHeight;

    if (originWidth > maxWidth || originHeight > maxHeight) {
      if (originWidth / originHeight > maxWidth / maxHeight) {
        targetWidth = maxWidth;
        targetHeight = Math.round(maxWidth * (originHeight / originWidth));
      } else {
        targetHeight = maxHeight;
        targetWidth = Math.round(maxHeight * (originWidth / originHeight));
      }
    }

    // set canvas size
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.drawImage(
        image,
        (crop.x || 0) * scaleX,
        (crop.y || 0) * scaleY,
        (crop.width || 0) * scaleX,
        (crop.height || 0) * scaleY,
        0,
        0,
        targetWidth,
        targetHeight
      );

      return new Promise(() => {
        canvas.toBlob(blob => {
          // Error if the canvas is empty
          if (!blob) {
            console.error("No image found within Canvas");

            return;
          }

          onComplete(blobToFile(blob, image.name));
        }, "image/png", 1);
      });
    }

    return;
  }, [ imgData, onComplete ]);

  /**
   * Handle ReactCrop's onImageLoaded functionality - A callback that happens
   * when the image is loaded.
   *
   * Centers the initial crop of the image (cropInsetPercentage)
   */
  const handleOnImageLoaded = React.useCallback(
    (img: HTMLImageElement) => {
      let x = 0;
      let y = 0;
      let width = img.width;
      let height = img.height;

      // Update the img state
      setImgData(img);

      // Set the width and height of the crop according to the given aspect
      if (img.width >= img.height) {
        // There's more play in the width - leave the height alone
        width = img.height * aspect * cropInsetPercentage;
        height = img.height * cropInsetPercentage;
      } else {
        // There's more play in the height - leave the width alone
        width = img.width * cropInsetPercentage;
        height = (img.width / aspect) * cropInsetPercentage;
      }

      // if calculated width ends up greater than, use img width
      if (width > img.width) {
        width = img.width * 0.95;
      }

      // Set the x and y axis (center the crop)
      x = (img.width - width) / 2;
      y = (img.height - height) / 2;

      const updatedCropData = {
        ...crop,
        x,
        y,
        width,
        height
      };

      // Update the crop state
      setCrop(updatedCropData);
      // Call the on complete functionality and generate the cropped file
      callOnComplete(updatedCropData, img);

      return false;
    },
    [
      cropInsetPercentage,
      crop,
      aspect,
      callOnComplete
    ]
  );

  /**
   * Handle ReactCrop's onComplete functionality - A callback that happens
   * after a resize, drag or nudge.
   *
   * Generates a cropped version of the image as a Blob
   */
  const handleOnComplete = React.useCallback(
    (crop: ReactCrop.Crop) => {
      // Call the on complete functionality and generate the cropped file
      callOnComplete(crop);
    },
    [ callOnComplete ]
  );

  // On mount
  React.useEffect(() => {
    // Action only if imageDataUrl is not set
    if (!imageDataUrl) {
      // Convert the image file to a string and update the imageDataUrl state
      fileToString(src)
        .then(convertedFile => setImageDataUrl(convertedFile))
        .catch(err => console.error(err));
    }
  }, [ imageDataUrl ]);

  // On aspect change, update the crop state
  React.useEffect(() => {
    const width = crop.height ? crop.height * aspect : undefined;

    // Update the crop state
    setCrop({
      ...crop,
      width,
      aspect
    });
  }, [ aspect, imageDataUrl ]);

  if (!imageDataUrl) {
    return null;
  }

  return (
    <ReactCrop
      src={imageDataUrl}
      crop={crop}
      onChange={newCrop => setCrop(newCrop)}
      onImageLoaded={handleOnImageLoaded}
      onComplete={handleOnComplete}
    />
  );
};
